import React from 'react'
import { Box, IconButton, Slider, Typography } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import Replay10Icon from '@mui/icons-material/Replay10'
import Forward10Icon from '@mui/icons-material/Forward10'

import lang from '@/lang'

/**
 * FLAG:useDocuAi — transport bar for the drawer's media preview.
 *
 * Ported from the DMS admindashboard's `MediaControls`
 * (`dms-s3/frontend/admindashboard/src/components/filelist/subcomponents/MediaControls.tsx`)
 * so both surfaces present the same player. Same control set and order:
 * play/pause · −10s · +10s · mute · volume · elapsed/total · scrubber · fullscreen.
 *
 * Differences from the original, all deliberate:
 *  - labels come from `lang` (omxweb is German; the admindashboard is English-only),
 *  - the trailing "Video player with custom controls" caption is dropped — it labels a
 *    dev tool, not a customer-facing drawer,
 *  - it is presentational only: the owning component holds the media element ref and state
 *    (see document-drawer-preview-media.tsx for why the original's getElementById is unsafe here).
 */

export interface MediaControlsProps {
  isPlaying: boolean
  currentTime: number
  duration: number
  volume: number
  isMuted: boolean
  isFullscreen: boolean
  onPlayPause: () => void
  onSeek: (time: number) => void
  onVolumeChange: (volume: number) => void
  onMute: () => void
  onFullscreen: () => void
  onSkipBackward: () => void
  onSkipForward: () => void
}

/** mm:ss, or h:mm:ss once the file passes an hour. Mirrors the admindashboard's `formatTime`. */
export const formatTime = (time: number): string => {
  if (!Number.isFinite(time) || time < 0) return '0:00'
  const hours = Math.floor(time / 3600)
  const minutes = Math.floor((time % 3600) / 60)
  const seconds = Math.floor(time % 60)
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

const DocumentDrawerMediaControls: React.FC<MediaControlsProps> = ({
  isPlaying,
  currentTime,
  duration,
  volume,
  isMuted,
  isFullscreen,
  onPlayPause,
  onSeek,
  onVolumeChange,
  onMute,
  onFullscreen,
  onSkipBackward,
  onSkipForward,
}) => {
  const l = lang.documents.mediaPlayer

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 2,
      p: 1,
      bgcolor: 'grey.100',
      borderRadius: 1,
      flexWrap: 'wrap',
      border: '1px solid #ddd',
    }}>
      <IconButton onClick={onPlayPause} size='small' title={isPlaying ? l.pause : l.play}>
        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
      </IconButton>

      <IconButton onClick={onSkipBackward} size='small' title={l.skipBackward}>
        <Replay10Icon />
      </IconButton>
      <IconButton onClick={onSkipForward} size='small' title={l.skipForward}>
        <Forward10Icon />
      </IconButton>

      <IconButton onClick={onMute} size='small' title={isMuted ? l.unmute : l.mute}>
        {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
      </IconButton>
      {/* Max 2.0, not 1.0: `media.volume` is capped at 1 by spec, so a quiet recording cannot be
          raised at all within the normal range. Everything above 1.0 is applied as Web Audio
          gain by the owner (see applyVolume). 1.0 stays the natural level — the extra travel is
          boost, not the default. */}
      <Box sx={{ width: 80, mx: 1 }}>
        <Slider
          aria-label={l.volume}
          title={`${l.volume}: ${Math.round((isMuted ? 0 : volume) * 100)}%`}
          value={isMuted ? 0 : volume}
          onChange={(_, newValue) => onVolumeChange(newValue as number)}
          min={0}
          max={2}
          step={0.05}
          marks={[{ value: 1 }]}
          size='small'
          sx={{ color: 'primary.main' }}
        />
      </Box>

      <Typography variant='caption' sx={{ minWidth: 100, fontSize: '0.7rem' }}>
        {formatTime(currentTime)} / {formatTime(duration)}
      </Typography>

      <Box sx={{ flexGrow: 1, mx: 2 }}>
        <Slider
          aria-label={l.progress}
          value={duration > 0 ? (currentTime / duration) * 100 : 0}
          onChange={(_, newValue) => onSeek(((newValue as number) / 100) * duration)}
          min={0}
          max={100}
          step={0.1}
          size='small'
          sx={{ color: 'secondary.main' }}
        />
      </Box>

      <IconButton onClick={onFullscreen} size='small' title={isFullscreen ? l.exitFullscreen : l.fullscreen}>
        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </IconButton>
    </Box>
  )
}

export default DocumentDrawerMediaControls
